import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from '../../../../services/message-service/message.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { BetriebskontakteService } from '../betriebskontakte.service';
import { BetriebSuchResult, Betriebskontakt, isVvvoPlaceholderBezeichnung } from '../betriebskontakt';
import { KieneUtils } from '../../../../utils/KieneUtils';
import {
    BetriebskontakteNeuerKontaktDialogComponent,
    NeuerKontaktDialogResult,
} from '../betriebskontakte-neuer-kontakt-dialog.component';
import { BetriebskontakteBearbeitenDialogComponent } from '../betriebskontakte-bearbeiten-dialog.component';

@Component({
    selector: 'kiene-betriebskontakte-erstellen-dialog',
    templateUrl: './betriebskontakte-erstellen-dialog.component.html',
    styleUrls: ['./betriebskontakte-erstellen-dialog.component.scss'],
})
export class BetriebskontakteErstellenDialogComponent implements OnInit, OnDestroy {
    @Input() zeigeSuche: boolean = false;
    @Input() initialVvvo: string = '';
    @Output() kontaktHinzugefuegt = new EventEmitter<void>();
    @Output() schliesseSuche = new EventEmitter<void>();

    suchErgebnisse: BetriebSuchResult[] = [];
    ladeSuche = false;
    ladeErstelleKontakt = false;
    suchControl = new FormControl('');
    private destroy$ = new Subject<void>();

    constructor(
        private betriebskontakteService: BetriebskontakteService,
        private messageService: MessageService,
        private dialog: MatDialog,
        private localStorageService: LocalStorageService,
    ) {}

    ngOnInit(): void {
        // Wenn eine VVVO-Nummer übergeben wurde, setze sie in das Suchfeld
        if (this.initialVvvo) {
            this.suchControl.setValue(this.initialVvvo);
        }
        this.setupSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Verhindert die Eingabe von Nicht-Zahlen
     */
    onKeyPress(event: KeyboardEvent): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        // Erlaube nur Zahlen (0-9)
        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
            return false;
        }
        return true;
    }

    /**
     * Verhindert das Einfügen von Nicht-Zahlen
     */
    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const clipboardData = event.clipboardData;
        if (clipboardData) {
            const pastedText = clipboardData.getData('text');
            // Entferne alle Nicht-Zahlen und begrenze auf 15 Zeichen
            const numbersOnly = pastedText.replace(/[^0-9]/g, '').substring(0, 15);
            if (numbersOnly) {
                this.suchControl.setValue(numbersOnly);
                // Automatische Suche wenn 15 Zahlen eingefügt wurden
                if (numbersOnly.length === 15) {
                    // Die valueChanges Subscription wird automatisch ausgelöst
                }
            }
        }
    }

    isVvvoPlatzhalter(betrieb: BetriebSuchResult): boolean {
        return isVvvoPlaceholderBezeichnung(betrieb.bezeichnung);
    }

    istBereitsKontakt(betrieb: BetriebSuchResult): boolean {
        return Number(betrieb.bereits_kontakt || 0) > 0;
    }

    private setupSearch(): void {
        this.suchControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(value => {
                    if (value && value.length === 15) {
                        this.ladeSuche = true;
                        return this.betriebskontakteService.sucheBetriebe(value);
                    } else {
                        this.suchErgebnisse = [];
                        return of({ records: [], count: 0 });
                    }
                }),
            )
            .subscribe({
                next: response => {
                    this.suchErgebnisse = (response.records || []).map(betrieb => ({
                        ...betrieb,
                        bereits_kontakt: Number(betrieb.bereits_kontakt || 0),
                    }));
                    this.ladeSuche = false;
                },
                error: error => {
                    this.ladeSuche = false;
                },
            });
    }

    fuegeKontaktHinzu(betrieb: BetriebSuchResult): void {
        this.ladeErstelleKontakt = true;
        this.betriebskontakteService
            .fuegeKontaktHinzu(betrieb.kunde_id)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.ladeErstelleKontakt = false;
                }),
            )
            .subscribe({
                next: response => {
                    if (response && response.fehler) {
                        this.messageService.errorMessage('Fehler beim Hinzufügen des Kontakts', response.fehler);
                        return;
                    }

                    this.messageService.infoMessage('Kontakt erfolgreich hinzugefügt');

                    const betriebIndex = this.suchErgebnisse.findIndex(b =>
                        KieneUtils.sameId(b.kunde_id, betrieb.kunde_id),
                    );
                    if (betriebIndex >= 0) {
                        this.suchErgebnisse[betriebIndex].bereits_kontakt = 1;
                        this.suchErgebnisse[betriebIndex].kontakt_id = response?.kontakt_id;
                    }

                    this.kontaktHinzugefuegt.emit();

                    if (this.isVvvoPlatzhalter(betrieb)) {
                        this.oeffnePlatzhalterBearbeiten(betrieb, response?.kontakt_id);
                    } else {
                        this.suchControl.setValue('');
                        this.suchErgebnisse = [];
                    }
                },
                error: error => {
                    this.messageService.errorMessage('Fehler beim Hinzufügen des Kontakts', error);
                },
            });
    }

    /**
     * VVVO-Platzhalter (Pass-Scan ohne Namen): Name/Adresse korrigieren.
     */
    korrigierePlatzhalter(betrieb: BetriebSuchResult): void {
        if (!this.isVvvoPlatzhalter(betrieb)) {
            return;
        }
        if (this.istBereitsKontakt(betrieb) && betrieb.kontakt_id) {
            this.oeffnePlatzhalterBearbeiten(betrieb, betrieb.kontakt_id);
            return;
        }
        this.fuegeKontaktHinzu(betrieb);
    }

    private oeffnePlatzhalterBearbeiten(betrieb: BetriebSuchResult, kontaktId?: number): void {
        const currentUserId = Number(this.localStorageService.getCurrentUser()?.benutzer_id ?? 0);
        const kontakt: Betriebskontakt = {
            kontakt_id: Number(kontaktId || betrieb.kontakt_id || 0),
            benutzer_id: Number(betrieb.benutzer_id || currentUserId),
            kunde_id: Number(betrieb.kunde_id),
            favorit: Number(betrieb.favorit || 0),
            ist_kunde: Number(betrieb.ist_kunde || 0),
            bezeichnung: betrieb.bezeichnung,
            zusatz: betrieb.zusatz,
            vvvo: betrieb.vvvo,
            email: betrieb.email,
            telefon_1: betrieb.telefon_1,
            telefon_2: betrieb.telefon_2,
            mobil_1: betrieb.mobil_1,
            mobil_2: betrieb.mobil_2,
            strasse: betrieb.strasse,
            plz: betrieb.plz,
            ort: betrieb.ort,
            ortsteil: betrieb.ortsteil,
            bezeichnung_final: betrieb.bezeichnung_final || betrieb.bezeichnung,
            zusatz_final: betrieb.zusatz_final,
            betriebsart_id: betrieb.betriebsart_id,
            erstellt_von: Number(betrieb.erstellt_von || currentUserId),
        };

        if (!kontakt.kontakt_id) {
            this.messageService.infoMessage(
                'Kontakt konnte nicht bearbeitet werden. Keine Kontakt-ID – bitte Liste neu laden.',
            );
            return;
        }

        const dialogRef = this.dialog.open(BetriebskontakteBearbeitenDialogComponent, {
            width: 'min(760px, 96vw)',
            maxWidth: '96vw',
            data: { kontakt },
        });

        dialogRef.afterClosed().subscribe(() => {
            this.kontaktHinzugefuegt.emit();
            this.suchControl.setValue('');
            this.suchErgebnisse = [];
        });
    }

    oeffneNeuenKontaktDialog(): void {
        const dialogRef = this.dialog.open(BetriebskontakteNeuerKontaktDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            data: { vvvo: this.suchControl.value },
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result: NeuerKontaktDialogResult) => {
            if (result && result.success) {
                this.kontaktHinzugefuegt.emit();
                this.suchControl.setValue('');
                this.suchErgebnisse = [];
                if (result.openEdit && result.kontakt) {
                    this.dialog.open(BetriebskontakteBearbeitenDialogComponent, {
                        width: 'min(760px, 96vw)',
                        maxWidth: '96vw',
                        data: { kontakt: result.kontakt },
                    }).afterClosed().subscribe(() => this.kontaktHinzugefuegt.emit());
                }
            }
        });
    }

    onAbbrechen(): void {
        this.schliesseSuche.emit();
    }
}
